import React from 'react';

import {
  Alert,
  Chip,
  GridLegacy as Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateSlashes } from '~/services/TimeService';

import type { ExistingSurchargeDateBlock, ResolvedSurchargeBlock } from '~/types/surcharges';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

interface Props {
  existingSurcharges: Array<ExistingSurchargeDateBlock>;
  resolvedBlocks: Array<ResolvedSurchargeBlock>;
  nameById: Map<string, string>;
}

export function SurchargeComparisonTable({ existingSurcharges, resolvedBlocks, nameById }: Props) {
  if (existingSurcharges.length === 0 && resolvedBlocks.length === 0) {
    return null;
  }

  const hasExisting = existingSurcharges.length > 0;

  return (
    <Stack spacing={2}>
      {hasExisting && (
        <Alert severity="info">
          {existingSurcharges.length} existing{' '}
          {existingSurcharges.length === 1 ? 'surcharge block' : 'surcharge blocks'} will be replaced by{' '}
          {resolvedBlocks.length} new resolved {resolvedBlocks.length === 1 ? 'block' : 'blocks'}.
        </Alert>
      )}

      <Grid container spacing={2}>
        {hasExisting && (
          <Grid item xs>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Existing surcharges (to be deleted)
            </Typography>
            <Paper sx={{ overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 260 }}>
                <Table stickyHeader size="small">
                  <TableHead
                    sx={{
                      bgcolor: 'error.dark',
                      '& th': { bgcolor: 'inherit', color: 'white' },
                    }}
                  >
                    <TableRow>
                      <TableCell>Range</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell align="right">Sell</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {existingSurcharges.map((block) => (
                      <TableRow
                        key={block.id}
                        sx={{
                          bgcolor: 'error.50',
                          '& td': { color: 'error.dark' },
                        }}
                      >
                        <TableCell>
                          {formatDateSlashes(block.start_date)} → {formatDateSlashes(block.end_date)}
                        </TableCell>
                        <TableCell>{daysOfWeekMessage(block)}</TableCell>
                        <TableCell align="right">
                          {block.cost_currency} {block.cost_amount}
                        </TableCell>
                        <TableCell align="right">
                          {block.cost_currency} {block.prices[block.cost_currency] ?? Object.values(block.prices)[0]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        <Grid item xs>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            New resolved blocks (to be created)
          </Typography>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    bgcolor: 'success.dark',
                    '& th': { bgcolor: 'inherit', color: 'white' },
                  }}
                >
                  <TableRow>
                    <TableCell>Sources</TableCell>
                    <TableCell>Range</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Sell</TableCell>
                    <TableCell align="center">Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resolvedBlocks.map((block, index) => {
                    const sourceLabel = block.sourceIds.map((id) => nameById.get(id) ?? id).join(' + ');
                    return (
                      <TableRow
                        key={`${block.start_date}-${block.end_date}-${index}`}
                        sx={{
                          bgcolor: 'success.50',
                          '& td': { color: 'success.dark' },
                        }}
                      >
                        <TableCell title={sourceLabel}>
                          {sourceLabel.length > 40 ? `${sourceLabel.slice(0, 40)}…` : sourceLabel}
                        </TableCell>
                        <TableCell>
                          {formatDateSlashes(block.start_date)} → {formatDateSlashes(block.end_date)}
                        </TableCell>
                        <TableCell>{daysOfWeekMessage(block)}</TableCell>
                        <TableCell align="right">
                          {block.currency_code} {block.cost_price}
                        </TableCell>
                        <TableCell align="right">
                          {block.currency_code} {block.sell_price}
                        </TableCell>
                        <TableCell align="center">
                          {block.isMerged ? (
                            <Chip size="small" label="Merged" color="info" variant="outlined" />
                          ) : (
                            <Chip size="small" label="Single" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}
